import React, { useEffect, useState } from 'react';
import { AdAccount, getMyAdAccounts } from '../../api/ads/ad-account.api';
import { BrandLiveRateCard } from '../../api/payment/brand-live.api';
import { freeSeatsLabel } from '../../utils/brand-live';
import { orderableAccounts } from '../../utils/individual-order';
import BrandBookingDialog, { SHAPE_LABEL } from './brand-booking-dialog';
import { OrdererSanctionProvider, useOrdererSanction } from '../brand-sanction/orderer-sanction-context';
import { SANCTION_LOCK_HINT } from '../../utils/brand-sanction';

// Tab "Quảng bá" trên trang creator: bảng giá, vị trí còn chỗ, và nút đặt cho doanh nghiệp đã xác minh.

const formatVnd = (v: number) => `${Number(v || 0).toLocaleString('vi-VN')}đ`;

// Khai ngoài component cha (landmine remount): nút đặt đọc trạng thái khoá từ provider bọc quanh nó.
const PromoBookButton: React.FC<{ onOpen: () => void }> = ({ onOpen }) => {
  const { blocked } = useOrdererSanction();
  return (
    <div className="space-y-1">
      <button
        type="button"
        disabled={blocked}
        title={blocked ? SANCTION_LOCK_HINT : undefined}
        onClick={onOpen}
        className="rounded-lg bg-[#0057FF] px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
      >
        Đặt quảng cáo
      </button>
      {blocked && <p className="text-[12px] text-rose-600 dark:text-rose-300">{SANCTION_LOCK_HINT}</p>}
    </div>
  );
};

interface Props {
  creatorId: string;
  creatorName: string;
  rateCard: BrandLiveRateCard;
  /** Chưa đăng nhập thì không tải tài khoản doanh nghiệp. */
  loggedIn: boolean;
}

const CreatorPromoTab: React.FC<Props> = ({ creatorId, creatorName, rateCard, loggedIn }) => {
  const [bizAccounts, setBizAccounts] = useState<AdAccount[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!loggedIn) return;
    getMyAdAccounts()
      .then((accs) => setBizAccounts(orderableAccounts(accs)))
      .catch(() => setBizAccounts([]));
  }, [loggedIn]);

  const hasPrice =
    !!rateCard.postPriceVnd || !!rateCard.liveMonthlyPriceVnd || !!rateCard.liveHourlyPriceVnd;

  return (
    <div className="space-y-4 py-2">
      <div className="space-y-2 rounded-xl border ui-divider p-4">
        <h3 className="font-semibold ui-text">Bảng giá quảng bá</h3>
        {!hasPrice && <p className="text-sm ui-muted">Creator chưa công bố giá.</p>}
        <ul className="space-y-1 text-sm ui-text">
          {!!rateCard.postPriceVnd && <li>Đăng bài: {formatVnd(rateCard.postPriceVnd)}</li>}
          {!!rateCard.liveMonthlyPriceVnd && (
            <li>
              Livestream gói tháng: {formatVnd(rateCard.liveMonthlyPriceVnd)} cho {rateCard.liveMonthlyHours} giờ
            </li>
          )}
          {!!rateCard.liveHourlyPriceVnd && (
            <li>
              Livestream theo giờ: {formatVnd(rateCard.liveHourlyPriceVnd)}/giờ, mua tối thiểu {rateCard.minHours} giờ
            </li>
          )}
        </ul>
      </div>

      {rateCard.slots.length > 0 && (
        <div className="space-y-2 rounded-xl border ui-divider p-4">
          <h3 className="font-semibold ui-text">Vị trí trên livestream</h3>
          <ul className="space-y-1 text-sm ui-text">
            {rateCard.slots.map((s) => (
              <li key={s.id} className="flex flex-wrap gap-x-2">
                <span className="font-medium">{s.name}</span>
                <span className="ui-muted">
                  {SHAPE_LABEL[s.shape] ?? s.shape} · {freeSeatsLabel(s.freeSeats, s.maxAds)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Chỉ người có tài khoản đặt đơn mới gọi brand-sanctions/mine. */}
      <OrdererSanctionProvider enabled={bizAccounts.length > 0}>
        {bizAccounts.length > 0 && hasPrice && <PromoBookButton onOpen={() => setOpen(true)} />}

        {open && (
          <BrandBookingDialog
            creatorId={creatorId}
            creatorName={creatorName}
            rateCard={rateCard}
            bizAccounts={bizAccounts}
            onClose={() => setOpen(false)}
            onCreated={() => setOpen(false)}
          />
        )}
      </OrdererSanctionProvider>
    </div>
  );
};

export default CreatorPromoTab;
